import React from 'react';
import { ProgressPhase } from '../types/interview';
import styles from '../styles/ProgressIndicator.module.css';
import { useUiCopy } from '../i18n/useUiCopy';

// --- SVG Icons ---
const VerificationIcon: React.FC = () => (
    <svg className={styles.stepIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline>
  </svg>
);
const InterviewIcon: React.FC = () => (
    <svg className={styles.stepIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
  </svg>
);
const AnalysisIcon: React.FC = () => (
    <svg className={styles.stepIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10"></line><line x1="12" y1="20" x2="12" y2="4"></line><line x1="6" y1="20" x2="6" y2="14"></line>
  </svg>
);
const CompletedIcon: React.FC = () => (
    <svg className={styles.stepIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12"></polyline>
    </svg>
);

interface ProgressIndicatorProps {
  currentPhase: ProgressPhase;
}

interface Step {
  id: string;
  label: string;
  icon: React.ComponentType;
}

const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({ currentPhase }) => {
  const copy = useUiCopy();
  const steps: Step[] = [
    { id: 'verification', label: copy.steps.verification, icon: VerificationIcon },
    { id: 'interview', label: copy.steps.interview, icon: InterviewIcon },
    { id: 'analysis', label: copy.steps.analysis, icon: AnalysisIcon }
  ];
  const getCurrentPhaseIndex = (): number => {
    switch (currentPhase) {
      case 'WAITING_APP_ID':
      case 'VALIDATING_ID':
      case 'VERIFICATION':
        return 0; // Corresponds to the 'Verification' step
      case 'STANDARD':
        return 1; // Corresponds to the 'Interview' step
      case 'COMPLETE':
      case 'RESULTS':
        return 2; // Corresponds to the 'Analysis' step
      default:
        return -1; // Should not happen in a normal flow
    }
  };

  const currentPhaseIndex = getCurrentPhaseIndex();

  const getStepStatus = (stepIndex: number): 'active' | 'completed' | 'pending' => {
    if (stepIndex < currentPhaseIndex) {
      return 'completed';
    }
    if (stepIndex === currentPhaseIndex) {
      return 'active';
    }
    return 'pending';
  };

  const getLineStatus = (index: number): 'completed' | 'pending' => {
    // The line is complete if the step *before* it is complete.
    const stepStatus = getStepStatus(index);
    return stepStatus === 'completed' ? 'completed' : 'pending';
  };

  return (
    <div className={styles.progressIndicatorContainer}>
      {steps.map((step, index) => (
        <React.Fragment key={step.id}>
          <div className={`${styles.progressStep} ${styles[getStepStatus(index)]}`}>
            <div className={styles.stepCircle}>
              {getStepStatus(index) === 'completed' ? <CompletedIcon /> : <step.icon />}
            </div>
            <div className={styles.stepLabel}>{step.label}</div>
          </div>

          {index < steps.length - 1 && (
            <div className={`${styles.progressLine} ${styles[getLineStatus(index)]}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

export default ProgressIndicator;
